import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { TokenContext } from '@/Contexts/TokenContext';
import { ApplicationAnswerProps } from '@/models/Application';
import { ApplicationCycle } from '@/models/ApplicationCycle';
import {
  ApplicationTemplateQuestionApiResponse,
  ApplicationTemplateQuestionProps,
} from '@/models/ApplicationTemplate';
import { EventProps } from '@/models/Event';
import { EventTypeProps } from '@/models/EventType';
import { getLocalizedText, LocalizedText } from '@/models/LocalizedText';
import { ProgramNames } from '@/models/Program';
import { QuestionProps } from '@/models/Question';
import { Locales } from '@/models/User';
import { baseUrl, DatePickerRange, defaultMaxDate, defaultMinDate } from '@/utils/constants';

import { useCustomFetch } from './useCustomFetch';
import usePreviewAnswer from './usePreviewAnswer';
import { QuestionChoiceProps } from '@/models/QuestionChoice';

function normalizeQuestionChoice(choice: unknown, lang: string): QuestionChoiceProps | null {
  if (typeof choice !== 'object' || choice == null) {
    return null;
  }

  const choiceRecord = choice as Record<string, unknown>;
  const choiceId = choiceRecord.choiceId;
  const choiceOrder = choiceRecord.choiceOrder;

  if (typeof choiceId !== 'number' || typeof choiceOrder !== 'number') {
    return null;
  }

  const translatedChoice = getLocalizedText(
    (choiceRecord.choiceTranslations as LocalizedText | undefined) ??
      (choiceRecord.choice as LocalizedText | undefined),
    lang
  );

  return {
    choiceId,
    choiceOrder,
    choice: translatedChoice,
  };
}

export default function useQuestions() {
  const { currentApplication, allRequiredQuestions, isPreviewMode, answers, updateAnswers } =
    useContext(CurrentApplicationContext);
  const { customFetch, customFetchResponse } = useCustomFetch();
  const navigate = useNavigate();
  const { authResult } = useContext(TokenContext);

  const { createPreviewEventSaveResponse, removePreviewAttachmentById } = usePreviewAnswer({
    isPreviewMode,
    fetchedAnswers: answers,
    fetchedQuestions: null,
    fetchedApplicationId: currentApplication.id,
    updateAnswers,
  });

  const getQuestions = useCallback(
    async (
      applicationTemplateId: number,
      locale: string
    ): Promise<ApplicationTemplateQuestionProps | null> => {
      let lang;
      let questionsUrl;
      if (!['es', 'fr', 'pt', 'it'].includes(locale)) {
        lang = 'en';
        questionsUrl = `${baseUrl}applicationtemplate/questions/${applicationTemplateId}`;
      } else {
        lang = locale.slice(0, 2);
        questionsUrl = `${baseUrl}applicationtemplate/questions/${applicationTemplateId}?language=${lang}`;
      }
      try {
        const response = await customFetch<ApplicationTemplateQuestionApiResponse>(questionsUrl, {
          method: 'GET',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Bearer ${authResult.accessToken}`,
          },
        });

        const normalizedResponse: ApplicationTemplateQuestionProps = {
          ...response,
          standards: response.standards.map((standard) => ({
            ...standard,
            standardName: getLocalizedText(standard.standardName, lang),
            standardDescription: getLocalizedText(standard.standardDescription, lang),
            questions: standard.questions.map((question) => ({
              ...question,
              questionText: getLocalizedText(
                question.questionTextTranslations ?? question.questionText,
                lang
              ),
              questionNote: getLocalizedText(
                question.questionNoteTranslations ?? question.questionNote,
                lang
              ),
              questionChoices: (question.questionChoices ?? [])
                .map((choice) => normalizeQuestionChoice(choice, lang))
                .filter((choice): choice is QuestionChoiceProps => choice != null),
            })),
          })),
        };

        return normalizedResponse;
      } catch {
        navigate('/error');
        return null;
      }
    },
    [authResult, customFetch, navigate]
  );

  const getQuestionAnswers = useCallback(
    async (appId: number, locale: Locales | null): Promise<ApplicationAnswerProps[]> => {
      let url;
      if (locale != null && ['esES', 'frFR', 'ptBR'].includes(locale)) {
        url = `${baseUrl}applications/dynamic/${appId}?language=${locale.slice(0, 2)}`;
      } else {
        url = `${baseUrl}applications/dynamic/${appId}`;
      }

      const response = await customFetch<ApplicationAnswerProps[]>(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      let resps: ApplicationAnswerProps[] = [];

      // TODO(bug): the only caller is CurrentApplicationProvider, which calls useQuestions() inside
      // itself and therefore reads the *default* CurrentApplicationContext, where
      // allRequiredQuestions is always null. So the responseString_foreign -> responseString swap
      // below never runs in the app, and es/fr/pt users see the English responseString for text
      // answers. Expected: do the locale swap unconditionally (it doesn't depend on required qs).
      if (allRequiredQuestions != null) {
        const req = Object.keys(allRequiredQuestions);
        const newObj: Record<string, '1' | null> = {};
        for (const questionId of req) {
          newObj[questionId] = null;
        }
        resps = response.map((resp: ApplicationAnswerProps) => {
          if (req.includes(resp.questionId.toString())) {
            if (
              resp.responseString != null ||
              resp.responseDate != null ||
              resp.responseDecimal != null ||
              resp.responseFile != null ||
              resp.responseInt != null ||
              resp.choices.length > 0
            ) {
              newObj[resp.questionId.toString()] = '1';
            }
          }
          //as of 8/26 the questionResponse endpoint returns a separate responseString_foreign key, which needs to be swapped into responseString in order to show up when locale changes
          if (locale != null && ['esES', 'frFR', 'ptBR'].includes(locale)) {
            resp.responseString = resp.responseString_foreign;
          }
          return resp;
        });
      } else {
        resps = response.map((resp: ApplicationAnswerProps) => resp);
      }

      return resps;
    },
    [authResult, allRequiredQuestions, customFetch]
  );

  const getStateSpecificQuestionAnswers = useCallback(
    async (appId: number): Promise<ApplicationAnswerProps[]> => {
      const url = `${baseUrl}applications/dynamic/${appId}`;

      const response = await customFetch<ApplicationAnswerProps[]>(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response;
    },
    [authResult, customFetch]
  );

  const getDownloadFile = useCallback(
    async (fileName: string) => {
      const url = `${baseUrl}files/download?filename=${encodeURIComponent(fileName)}`;
      const response = await customFetchResponse(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });
      const headers = response.headers;
      // TODO(bug): 'contentType' is not the Content-Type header name, so this is always null
      // (unless the API sends a literal custom `contentType` header) and DataUpload re-wraps the
      // blob with an empty MIME type (PDFs/images download instead of opening inline). Expected:
      // headers.get('content-type'). The response status is also not checked here.
      const contentType = headers.get('contentType');
      const blob = await response.blob();

      return { response: response, blob: blob, contentType: contentType };
    },
    [authResult, customFetchResponse]
  );

  const postAnswer = useCallback(
    async (
      applicationId: number,
      questionId: number,
      answer: string | number | Array<string | number> | null,
      dataType?: string,
      questionType?: string,
      locale?: Locales
    ): Promise<ApplicationAnswerProps | null> => {
      let url;
      if (locale != null && ['esES', 'frFR', 'ptBR'].includes(locale)) {
        url = `${baseUrl}applications/${applicationId}/questionresponse/${questionId}?language=${locale.slice(
          0,
          2
        )}`;
      } else {
        url = `${baseUrl}applications/${applicationId}/questionresponse/${questionId}`;
      }

      let responseValue: typeof answer | number | number[];
      if (answer === null) {
        responseValue = null;
      } else if (questionType === 'single select') {
        responseValue = [Number(answer)];
      } else if (
        questionType === 'multicheck' ||
        questionType === 'multiselect' ||
        questionType === 'multiselect-3'
      ) {
        responseValue = answer;
      } else if (dataType === 'int' || dataType === 'decimal') {
        responseValue = Number(answer);
      } else if (dataType === 'string') {
        responseValue = answer.toString();
      } else {
        responseValue = answer;
      }

      const postInfo = {
        applicationId: Number(applicationId),
        questionId: questionId,
        response: responseValue,
      };

      const response = await customFetch<ApplicationAnswerProps>(url, {
        method: 'PUT',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },

        body: JSON.stringify(postInfo),
      });

      return response;
    },
    [authResult, customFetch]
  );

  const getEventTypes = useCallback(
    async (locale: Locales): Promise<EventTypeProps[]> => {
      let url;
      if (['esES', 'frFR', 'ptBR'].includes(locale)) {
        url = `${baseUrl}eventtypes?language=${locale.slice(0, 2)}`;
      } else {
        url = `${baseUrl}eventtypes`;
      }

      const response = await customFetch<EventTypeProps[]>(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response;
    },
    [authResult, customFetch]
  );

  const getEvents = useCallback(
    async (applicationId: number) => {
      const url = `${baseUrl}events/?applicationid=${applicationId}`;

      const response = await customFetch(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response;
    },
    [authResult, customFetch]
  );

  const createEvent = useCallback(
    async (eventInfo: EventProps, questionId: number) => {
      if (isPreviewMode) {
        return createPreviewEventSaveResponse(eventInfo);
      }

      const payload = {
        ...eventInfo,
        questionId,
      };

      const response = await customFetch<{ id: number } | null>(`${baseUrl}events/Add`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      return response;
    },
    [authResult, createPreviewEventSaveResponse, customFetch, isPreviewMode]
  );

  const updateEvent = useCallback(
    async (eventInfo: EventProps) => {
      if (isPreviewMode) {
        return createPreviewEventSaveResponse(eventInfo);
      }

      const response = await customFetch<{ id: number } | null>(
        `${baseUrl}events/${eventInfo.id}`,
        {
          method: 'PUT',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Bearer ${authResult.accessToken}`,
          },
          body: JSON.stringify(eventInfo),
        }
      );

      // TODO: this api returns "" on update, and the new event id on create - need to standardize
      return response;
    },
    [authResult, createPreviewEventSaveResponse, customFetch, isPreviewMode]
  );

  const getSpecificQuestionAnswer = useCallback(
    async (questionId: number, appId?: string): Promise<ApplicationAnswerProps> => {
      let url = `${baseUrl}applications/dynamic/${currentApplication.id}`;
      if (appId != null) {
        url = `${baseUrl}applications/dynamic/${appId}`;
      }
      const response = await customFetch<ApplicationAnswerProps[]>(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      const filteredByQuestion = response.filter(
        (x: ApplicationAnswerProps) => x.questionId == questionId
      );

      return filteredByQuestion[0];
    },
    [authResult, currentApplication.id, customFetch]
  );

  const deleteFile = useCallback(
    async (attachmentId: number | null) => {
      if (attachmentId == null) return null;

      if (isPreviewMode) {
        removePreviewAttachmentById(attachmentId);
        return 'preview-file-deleted';
      }

      const url = `${baseUrl}files/delete/${attachmentId}`;
      const response = await customFetch(url, {
        method: 'DELETE',
        headers: {
          accept: '*/*',
          Authorization: `Bearer ${authResult.accessToken}`,
          'Cache-Control': 'no-cache',
        },
      });

      return response;
    },
    [authResult, customFetch, isPreviewMode, removePreviewAttachmentById]
  );

  const deleteEvent = useCallback(
    async (eventId: number) => {
      if (isPreviewMode) {
        return 'delete successful';
      }

      const url = `${baseUrl}events/${eventId}`;
      await customFetch(url, {
        method: 'DELETE',
        headers: {
          accept: '*/*',
          Authorization: `Bearer ${authResult.accessToken}`,
          'Cache-Control': 'no-cache',
        },
      });

      return 'delete successful';
    },
    [authResult, customFetch, isPreviewMode]
  );

  const getMinDate = useCallback(
    (question: QuestionProps | null, applicationCycle: ApplicationCycle | null): Dayjs => {
      if (question == null) return dayjs(defaultMinDate);

      if (applicationCycle == null) {
        return dayjs(defaultMinDate);
      }

      const year =
        applicationCycle.programId == ProgramNames.TreeLineUSA
          ? applicationCycle.year - 1
          : applicationCycle.year;

      switch (question.datePickerRangeId) {
        case DatePickerRange.AppYear: {
          return dayjs(new Date(year, 0, 1)); // January 1st of the application year
        }
        case DatePickerRange.AcademicAppYear: {
          return dayjs(new Date(year, 6, 1)); // July 1st of the application year
        }
        case DatePickerRange.UpToAcademicAppYearEnd:
        case DatePickerRange.UpToAppYearEnd:
        default: {
          return dayjs(defaultMinDate);
        }
      }
    },
    []
  );

  const getMaxDate = useCallback(
    (question: QuestionProps | null, applicationCycle: ApplicationCycle | null): Dayjs => {
      if (question == null) return dayjs(defaultMaxDate);

      if (applicationCycle == null) {
        return dayjs(defaultMaxDate);
      }

      const year =
        applicationCycle.programId == ProgramNames.TreeLineUSA
          ? applicationCycle.year - 1
          : applicationCycle.year;

      switch (question.datePickerRangeId) {
        case DatePickerRange.AppYear:
        case DatePickerRange.UpToAppYearEnd: {
          return dayjs(new Date(year, 11, 31)); // December 31st of the application year
        }
        case DatePickerRange.AcademicAppYear:
        case DatePickerRange.UpToAcademicAppYearEnd: {
          return dayjs(new Date(year + 1, 5, 30)); // June 30st of the next year
        }
        default: {
          return dayjs(defaultMaxDate);
        }
      }
    },
    []
  );

  const validDateTime = useCallback(
    (question: QuestionProps, applicationCycle: ApplicationCycle, date: dayjs.Dayjs | null) => {
      if (date == null) return false;

      // make sure the date is valid and between the min and max date
      return (
        dayjs(date).isValid() &&
        dayjs(date).isAfter(getMinDate(question, applicationCycle)) &&
        dayjs(date).isBefore(getMaxDate(question, applicationCycle))
      );
    },
    [getMinDate, getMaxDate]
  );

  return {
    getQuestions,
    getStateSpecificQuestionAnswers,
    getDownloadFile,
    postAnswer,
    getQuestionAnswers,
    getEventTypes,
    getEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    getSpecificQuestionAnswer,
    deleteFile,
    getMinDate,
    getMaxDate,
    validDateTime,
  };
}
